/**
 * @fileoverview Local-first CRUD for onboarding_drafts in system-web.
 * @module system-web/services/onboardingDraftService
 *
 * Responsibilities:
 * - Provide pure Dexie reads for onboarding drafts (list/get) — safe for
 *   liveQuery observed functions.
 * - Provide create/update/delete/mark-provisioned mutations with sync wakeup.
 * - Provide a one-time legacy-id migration (`migrateLegacyDrafts`) that MUST
 *   run from a mount effect, never from inside a liveQuery observed function.
 * - Compute the next SL99999xxx NERIS serial from local tenants + drafts.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, generateObjectId, isObjectIdHex,
 *   wakeLocalWrite)
 * - @firesquirrel/core-domain (isDeepEqual)
 * - ../pages/tenants/wizard/types (WizardState)
 *
 * Architecture:
 * - Local-first. Drafts live in the Dexie `onboarding_drafts` table.
 * - `listOnboardingDrafts` is a pure Dexie read — it NEVER writes, so it is
 *   safe inside a liveQuery observed function.
 * - `getOnboardingDraft` performs a lazy legacy-id migration on a direct
 *   deep-link access. This is safe because `getOnboardingDraft` is called
 *   from a `useEffect` (CreateTenantWizard), never from a liveQuery observed
 *   function. The bulk migration runs via `migrateLegacyDrafts()` on mount.
 * - Mutations (`createOnboardingDraft`, `updateOnboardingDraft`,
 *   `deleteOnboardingDraft`, `markDraftProvisioned`) write to Dexie and call
 *   `wakeLocalWrite('onboarding_drafts')`.
 *
 * Notes:
 * - The final `provision` step remains an API call (Type 3 — server-side
 *   process with side effects); it is not part of this service.
 * - Legacy-id migration is idempotent — drafts whose `_id` is already
 *   ObjectId-shaped are skipped.
 */

import { isDeepEqual } from '@firesquirrel/core-domain';
import { generateObjectId, getSystemDb, isObjectIdHex, wakeLocalWrite } from '@firesquirrel/local-db';
import { writeAuditLog } from './systemAuditLogService';
import type { WizardState } from '../pages/tenants/wizard/types';

/**
 * @contract OnboardingDraftRecord
 * Purpose: Local Dexie record for one onboarding wizard draft.
 * Fields:  _id — ObjectId-shaped id (legacy drafts used UUIDs and are
 *          migrated by `migrateLegacyDrafts`); wizardState — full wizard
 *          state snapshot; lastStep — highest step reached; status —
 *          'in_progress' | 'provisioned'; notes; createdAt; updatedAt.
 * Rules:   `_id` MUST be ObjectId-shaped. Legacy UUID-shaped ids are
 *          migrated once on mount via `migrateLegacyDrafts()`.
 * Sync:    Local-first; `onboarding_drafts` is a synced Dexie table.
 */
export interface OnboardingDraftRecord {
  _id: string;
  wizardState: WizardState;
  lastStep: number;
  status: 'in_progress' | 'provisioned';
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `onboarding_drafts` table)
 * Write Flow: read-only — pure Dexie read, safe for liveQuery observed
 *   functions. NEVER writes to Dexie.
 * Sync Strategy: standard pull-synced collection.
 * Conflict Resolution: n/a (pure read).
 *
 * List all non-deleted onboarding drafts sorted by `updatedAt` descending.
 * Pure read — does NOT migrate legacy ids. Legacy-id migration is handled
 * by `migrateLegacyDrafts()`, which MUST be called from a separate mount
 * effect, never from inside a liveQuery observed function.
 */
export async function listOnboardingDrafts(): Promise<OnboardingDraftRecord[]> {
  const db = await getSystemDb();
  const docs = await db.onboarding_drafts.toArray() as Array<OnboardingDraftRecord & Record<string, any>>;
  return docs
    .filter((d: any) => !d._deleted)
    .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `onboarding_drafts` table)
 * Write Flow: read-only for ObjectId-shaped ids; performs a lazy legacy-id
 *   migration (write) when the requested id is a legacy UUID-shaped id.
 *   This is safe because `getOnboardingDraft` is called from a `useEffect`
 *   (CreateTenantWizard deep-link), never from a liveQuery observed function.
 * Sync Strategy: standard pull-synced collection.
 * Conflict Resolution: n/a (single-reader access).
 *
 * Get a single onboarding draft by id. Returns `null` when the draft is
 * missing or soft-deleted. If the draft has a legacy UUID-shaped id, it is
 * migrated to an ObjectId-shaped id (the migrated draft is returned).
 */
export async function getOnboardingDraft(id: string): Promise<OnboardingDraftRecord | null> {
  const db = await getSystemDb();
  const doc = await db.onboarding_drafts.get(id);
  if (!doc || (doc as any)._deleted) return null;
  return migrateLegacyDraftId(doc as OnboardingDraftRecord & Record<string, any>);
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `onboarding_drafts` table)
 * Write Flow: scans all drafts → for each non-deleted draft with a
 *   non-ObjectId-shaped `_id`, writes a new migrated row + soft-deletes the
 *   legacy row → `wakeLocalWrite('onboarding_drafts')`.
 * Sync Strategy: one-time local migration; legacy rows are soft-deleted so
 *   the sync pipeline propagates the tombstones.
 * Conflict Resolution: NOT REQUIRED — idempotent; drafts already migrated
 *   are skipped.
 *
 * Migrate legacy non-ObjectId-shaped draft ids to ObjectId-shaped ids.
 * This is a one-time migration that MUST be called from a mount effect
 * (e.g. `useEffect(() => { void migrateLegacyDrafts(); }, [])`), NEVER from
 * inside a liveQuery observed function — it writes to Dexie and would
 * re-trigger the observer.
 *
 * Idempotency: REQUIRED — drafts whose `_id` is already ObjectId-shaped are
 * skipped. Re-running after migration is a no-op.
 */
export async function migrateLegacyDrafts(): Promise<void> {
  const db = await getSystemDb();
  const docs = await db.onboarding_drafts.toArray() as Array<OnboardingDraftRecord & Record<string, any>>;
  const legacyDocs = docs.filter((d: any) => !d._deleted && !isObjectIdHex(d._id));
  for (const doc of legacyDocs) {
    await migrateLegacyDraftId(doc);
  }
}

/**
 * Migrate a single legacy draft id to an ObjectId-shaped id. Writes a new
 * migrated row and soft-deletes the legacy row. Idempotent — ObjectId-shaped
 * ids are returned unchanged with no writes.
 *
 * NOT liveQuery-safe — writes to Dexie and calls `wakeLocalWrite`. Only call
 * from `getOnboardingDraft` (useEffect path) or `migrateLegacyDrafts` (mount
 * effect), never from a liveQuery observed function.
 */
async function migrateLegacyDraftId(doc: OnboardingDraftRecord & Record<string, any>): Promise<OnboardingDraftRecord> {
  if (doc._deleted || isObjectIdHex(doc._id)) {
    return doc;
  }

  const db = await getSystemDb();
  const now = new Date().toISOString();
  const migratedDraft: OnboardingDraftRecord & Record<string, any> = {
    ...doc,
    _id: generateObjectId(),
    updatedAt: now,
  };

  await db.onboarding_drafts.put(migratedDraft as unknown as Record<string, any>);
  await db.onboarding_drafts.put({
    ...doc,
    _deleted: true,
    deletedAt: now,
    updatedAt: now,
    migratedToDraftId: migratedDraft._id,
  });
  wakeLocalWrite('onboarding_drafts');

  return migratedDraft;
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `onboarding_drafts` table)
 * Write Flow: generate ObjectId-shaped id → Dexie put →
 *   `wakeLocalWrite('onboarding_drafts')`
 * Sync Strategy: write-through; the next sync push propagates the new draft.
 * Conflict Resolution: NOT REQUIRED — new id is unique.
 *
 * Create a new draft — generate ObjectId-shaped ID client-side and insert into Dexie.
 */
export async function createOnboardingDraft(wizardState: WizardState): Promise<OnboardingDraftRecord> {
  const now = new Date().toISOString();
  const draft: OnboardingDraftRecord = {
    _id: generateObjectId(),
    wizardState,
    lastStep: wizardState.currentStep,
    status: 'in_progress',
    createdAt: now,
    updatedAt: now,
  };

  const db = await getSystemDb();
  await db.onboarding_drafts.put(draft as unknown as Record<string, any>);
  wakeLocalWrite('onboarding_drafts');

  try {
    const name = wizardState?.departmentInfo?.name ?? 'Untitled';
    await writeAuditLog({
      collection: 'onboarding_drafts',
      documentId: draft._id,
      action: 'create',
      summary: `Created onboarding draft: ${name}`,
    });
  } catch { /* audit failure must not block mutation */ }

  return draft;
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `onboarding_drafts` table)
 * Write Flow: read existing → merge patch → Dexie put →
 *   `wakeLocalWrite('onboarding_drafts')`. Skips the write when the patch
 *   is a no-op (same lastStep + deep-equal wizardState).
 * Sync Strategy: write-through.
 * Conflict Resolution: NOT REQUIRED — single-writer per draft.
 */
export async function updateOnboardingDraft(
  id: string,
  patch: { wizardState: WizardState; lastStep: number },
): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.onboarding_drafts.get(id);
  if (existing) {
    if (
      existing.lastStep === patch.lastStep
      && isDeepEqual(existing.wizardState, patch.wizardState)
    ) {
      return;
    }

    const now = new Date().toISOString();
    await db.onboarding_drafts.put({
      ...(existing as any),
      wizardState: patch.wizardState,
      lastStep: patch.lastStep,
      updatedAt: now,
    });
    wakeLocalWrite('onboarding_drafts');
  }
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `onboarding_drafts` table)
 * Write Flow: read existing → soft-delete (`_deleted: true`, `deletedAt`) →
 *   Dexie put → `wakeLocalWrite('onboarding_drafts')`.
 * Sync Strategy: write-through; tombstone propagates via sync push.
 * Conflict Resolution: NOT REQUIRED — single-writer per draft.
 */
export async function deleteOnboardingDraft(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.onboarding_drafts.get(id);
  if (!existing) return;
  const now = new Date().toISOString();
  // G-016: deletedBy is required by the architecture.md delete() standard.
  // `onboarding_drafts` is local-only; deletedBy is the local actor.
  await db.onboarding_drafts.put({ ...(existing as any), _deleted: true, deletedAt: now, deletedBy: 'system-web', updatedAt: now });
  wakeLocalWrite('onboarding_drafts');

  try {
    const name = (existing as any).wizardState?.departmentInfo?.name ?? id;
    await writeAuditLog({
      collection: 'onboarding_drafts',
      documentId: id,
      action: 'delete',
      summary: `Deleted onboarding draft: ${name}`,
      changes: [{ field: '_deleted', oldValue: false, newValue: true, fieldType: 'boolean' }],
    });
  } catch { /* audit failure must not block mutation */ }
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `tenants` + `onboarding_drafts` tables)
 * Write Flow: read-only — pure Dexie read, safe for liveQuery observed
 *   functions.
 * Sync Strategy: reads synced collections.
 * Conflict Resolution: n/a (pure read).
 *
 * Next SL serial computation — local only, no server fallback.
 * Scans the local Dexie `tenants` and `onboarding_drafts` collections for the
 * highest existing SL99999xxx NERIS ID and returns the next serial.
 * If the local DB is empty or not synced, this will return 001 — which is
 * correct for a fresh system and a visible signal if sync is broken.
 */
export async function getNextSlSerial(): Promise<{ nextSerial: number; nextId: string }> {
  const db = await getSystemDb();
  const SL_REGEX = /^SL99999\d{3}$/;

  // Scan local tenants
  const tenants = await db.tenants.toArray() as Record<string, any>[];

  // Scan local drafts (exclude soft-deleted — their nerisId is no longer reserved)
  const drafts = (await db.onboarding_drafts.toArray() as OnboardingDraftRecord[]).filter((d: any) => !d._deleted);

  let maxSerial = 0;

  for (const t of tenants) {
    const nid = t.nerisId as string | undefined;
    if (nid && SL_REGEX.test(nid)) {
      const serial = parseInt(nid.slice(7), 10);
      if (serial > maxSerial) maxSerial = serial;
    }
  }

  for (const d of drafts) {
    const nid = d.wizardState?.departmentInfo?.nerisId;
    if (nid && SL_REGEX.test(nid)) {
      const serial = parseInt(nid.slice(7), 10);
      if (serial > maxSerial) maxSerial = serial;
    }
  }

  const nextSerial = maxSerial + 1;
  const nextId = `SL99999${String(nextSerial).padStart(3, '0')}`;
  return { nextSerial, nextId };
}

/**
 * @localFirst
 * Source of Truth: local (Dexie `onboarding_drafts` table)
 * Write Flow: read existing → set `status: 'provisioned'` → Dexie put →
 *   `wakeLocalWrite('onboarding_drafts')`.
 * Sync Strategy: write-through.
 * Conflict Resolution: NOT REQUIRED — single-writer per draft.
 */
export async function markDraftProvisioned(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.onboarding_drafts.get(id);
  if (existing) {
    const prevStatus = (existing as any).status;
    await db.onboarding_drafts.put({
      ...(existing as any),
      status: 'provisioned',
      updatedAt: new Date().toISOString(),
    });
    wakeLocalWrite('onboarding_drafts');

    try {
      const name = (existing as any).wizardState?.departmentInfo?.name ?? id;
      await writeAuditLog({
        collection: 'onboarding_drafts',
        documentId: id,
        action: 'update',
        summary: `Marked onboarding draft as provisioned: ${name}`,
        changes: [{ field: 'status', oldValue: prevStatus ?? 'in_progress', newValue: 'provisioned', fieldType: 'string' }],
      });
    } catch { /* audit failure must not block mutation */ }
  }
}
